/**
 * The meta seam of a run (doc 08 / doc 12 §Saves) — everything the run screen
 * owes the profile, in one place.
 *
 * There are exactly three moments: a payday banks Pearls and tower XP and then
 * snapshots the run in the SAME step, so nothing can ever be paid twice; the
 * player walking out snapshots without paying; and the run ending banks the
 * depth-clear XP, files a run-log entry (doc 18 §7) and clears the resume point.
 */

import { awardClear, awardDepthClear, awardXp, logRun, registerRunEnd, type Reveal } from '../meta/profile.ts';
import { activeTiers } from '../meta/pressure.ts';
import { PLACEABLE } from '../sim/defs.ts';
import type { Sim } from '../sim/sim.ts';
import type { SimEvent } from '../sim/types.ts';
import type { App } from './app.ts';

export interface RunResult {
  outcome: 'won' | 'lost';
  depth: number;
  fightsCleared: number;
  pearls: number;
  xp: Record<string, number>;
  reveal: Reveal | null;
  coreHp: number;
  coreMaxHp: number;
  ticks: number;
}

export class RunLedger {
  private readonly app: App;
  private readonly sim: Sim;
  /** Every tower def the player actually deployed — doc 08's depth-clear XP. */
  readonly deployed = new Set<string>();
  /** Shoal ids in pull order: the doc 14 question a playtest has to answer. */
  private readonly pulls: string[] = [];
  private pearls = 0;
  private readonly xp: Record<string, number> = {};

  constructor(app: App, sim: Sim) {
    this.app = app;
    this.sim = sim;
  }

  get pearlsBanked(): number {
    return this.pearls;
  }

  note(shoalId: string): void {
    this.pulls.push(shoalId);
  }

  /** Bank one payday. Returns the Pearls, or null when there is no profile. */
  bankClear(ev: Extract<SimEvent, { type: 'waveCleared' }>): number | null {
    const profile = this.app.profile;
    if (!profile) return null;
    const award = awardClear(profile, {
      depth: this.sim.scenario.depth,
      shoalId: ev.shoalId,
      shoalName: ev.name,
      riled: ev.riled,
      skips: ev.skips,
      killedSpecies: ev.killedSpecies,
      ...(ev.boss ? { boss: ev.boss } : {}),
    });
    this.pearls += award.pearls;

    // abilities and anything that is not a tower earn no tower XP (doc 08)
    const damage: Record<string, number> = {};
    for (const [def, amount] of Object.entries(ev.xpDamage)) {
      if (PLACEABLE[def]?.kind === 'tower') damage[def] = amount;
    }
    const killers = ev.xpKillers.filter((def) => PLACEABLE[def]?.kind === 'tower');
    for (const [def, gained] of Object.entries(awardXp(profile, damage, killers))) {
      this.xp[def] = (this.xp[def] ?? 0) + gained;
    }
    return award.pearls;
  }

  /** Save the run at a build phase — the resume point doc 12 promises. */
  snapshot(): string | null {
    const profile = this.app.profile;
    if (!profile || this.sim.state.outcome !== 'playing') return null;
    if (this.sim.phase !== 'build') return null; // still fighting: next clear will do it
    profile.run = {
      depth: this.sim.scenario.depth,
      seed: this.sim.state.seed,
      snapshot: this.sim.snapshot(),
      savedAt: Date.now(),
      fightsCleared: this.sim.state.fightsCleared,
    };
    return this.app.persist();
  }

  /** The run is over. Bank what is owed, file the log, drop the resume point. */
  finish(outcome: 'won' | 'lost'): RunResult {
    const profile = this.app.profile;
    let reveal: Reveal | null = null;
    if (profile) {
      if (outcome === 'won') {
        for (const [def, gained] of Object.entries(awardDepthClear(profile, [...this.deployed]))) {
          this.xp[def] = (this.xp[def] ?? 0) + gained;
        }
      }
      reveal = registerRunEnd(profile, outcome === 'won', this.sim.scenario.depth);
      this.file(outcome);
      delete profile.run; // the run is over: nothing left to resume
      this.app.persist();
    }
    const core = this.sim.byId(this.sim.state.coreId)!;
    return {
      outcome,
      depth: this.sim.scenario.depth,
      fightsCleared: this.sim.state.fightsCleared,
      pearls: this.pearls,
      xp: { ...this.xp },
      reveal,
      coreHp: Math.max(0, Math.ceil(core.hp)),
      coreMaxHp: core.maxHp,
      ticks: this.sim.state.tick,
    };
  }

  /** Doc 18 §7: one line in the ring buffer. Local, and never sent anywhere. */
  file(outcome: 'won' | 'lost' | 'left'): void {
    const profile = this.app.profile;
    if (!profile) return;
    const core = this.sim.byId(this.sim.state.coreId);
    const depth = this.sim.scenario.depth;
    logRun(profile, {
      depth,
      pulls: [...this.pulls],
      fightsCleared: this.sim.state.fightsCleared,
      towers: [...this.deployed],
      outcome,
      corePct: core ? Math.round((Math.max(0, core.hp) / core.maxHp) * 100) : 0,
      ticks: this.sim.state.tick,
      pressure: activeTiers(profile.pressure[String(depth)] ?? []).map((t) => t.id),
      assist: profile.settings.assist,
    });
  }
}
